import { useMemo } from 'react'

import { useGetRefreshedAnalysisQuery } from '../../../api/analysis'
import { useGetAnalysisAnswerFeaturesQuery } from '../../../api/analysis-screenshots'
import { useGetInternalGameDocumentQuery } from '../../../api/games'
import { useGetInternalConceptsQuery, useGetInternalTagsQuery } from '../../../api/tags'
import { InternalTagType } from '../../../types/InternalTag'
import { getAnswerFeatures, toConceptsById, toFeaturesByLegacyId, toScreenshotItems, toScreenshotView } from '../helpers/screenshotHelpers'

// The analysis is read on every visit: the screens write its whole screenshot list.
export const useAnalysisScreenshotsData = (analysisId: string) => {
  const analysisQuery = useGetRefreshedAnalysisQuery(analysisId, { skip: !analysisId, refetchOnMountOrArgChange: true })
  const analysis = analysisQuery.data
  const gameQuery = useGetInternalGameDocumentQuery(analysis?.gameId ?? '', { skip: !analysis?.gameId })
  const categoryIds = useMemo(() => Object.keys(analysis?.answers ?? {}).sort(), [analysis?.answers])
  const featuresQuery = useGetAnalysisAnswerFeaturesQuery(categoryIds, { skip: !analysis })
  const conceptsQuery = useGetInternalConceptsQuery()
  const tagsQuery = useGetInternalTagsQuery()

  const items = useMemo(() => toScreenshotItems(analysis?.screenshots), [analysis?.screenshots])
  const featuresByLegacyId = useMemo(() => toFeaturesByLegacyId(featuresQuery.data ?? []), [featuresQuery.data])
  const answerFeatures = useMemo(() => getAnswerFeatures(analysis?.answers, featuresQuery.data ?? []), [analysis?.answers, featuresQuery.data])
  const conceptTags = useMemo(() => (tagsQuery.data ?? []).filter((tag) => tag.type === InternalTagType.Concept), [tagsQuery.data])
  const conceptsById = useMemo(() => toConceptsById(conceptsQuery.data ?? [], conceptTags), [conceptsQuery.data, conceptTags])
  const views = useMemo(
    () => items.map((item, index) => toScreenshotView(item, index, featuresByLegacyId, conceptsById)),
    [items, featuresByLegacyId, conceptsById]
  )

  const isLoading = analysisQuery.isLoading || gameQuery.isLoading || featuresQuery.isLoading || conceptsQuery.isLoading || tagsQuery.isLoading
  const error = analysisQuery.isError
    ? 'analysis'
    : gameQuery.isError
    ? 'game'
    : featuresQuery.isError
    ? 'features'
    : conceptsQuery.isError || tagsQuery.isError
    ? 'concepts'
    : undefined

  return {
    analysis,
    game: gameQuery.data,
    items,
    views,
    answerFeatures,
    conceptsById,
    isLoading: isLoading || (!error && (!analysis || !gameQuery.data)),
    // True while a write's invalidation reads the analysis again.
    isRefreshing: analysisQuery.isFetching,
    error: error as 'analysis' | 'game' | 'features' | 'concepts' | undefined,
    refetch: analysisQuery.refetch,
  }
}
